import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, PackageSearch, ShoppingBag } from 'lucide-react';
import {
  listPendingProcurement,
  listProcurementBatches,
  getProcurementBatch,
  updateOrderLineCost,
} from '../../api/procurement';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card, CardHeader } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { CreateBatchModal } from './CreateBatchModal';
import type { PendingProcurementGroup, ProcurementBatch } from '../../types/api';

function money(value: number) {
  return `£${value.toFixed(2)}`;
}

// §22: cost editable inline, whether or not the item's been delivered yet -
// blurs/saves onto OrderLine.actualCost, distinct from priceSnapshot (what
// the customer's charged). Each row keeps its own draft text so typing in
// one line doesn't touch any other.
function CostCell({ orderLineId, actualCost }: { orderLineId: string; actualCost: string | null }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [draft, setDraft] = useState(actualCost ?? '');
  const [editing, setEditing] = useState(false);

  const mutation = useMutation({
    mutationFn: (value: number) => updateOrderLineCost(orderLineId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['procurement-batches'] });
      queryClient.invalidateQueries({ queryKey: ['procurement-batch'] });
      setEditing(false);
    },
    onError: (err) => showToast(apiErrorMessage(err, 'Could not save the cost.'), 'error'),
  });

  function save() {
    const value = Number(draft);
    if (draft.trim() === '' || Number.isNaN(value) || value < 0) {
      showToast('Enter a valid cost.', 'error');
      return;
    }
    mutation.mutate(value);
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => {
          setDraft(actualCost ?? '');
          setEditing(true);
        }}
        className="rounded px-1.5 py-0.5 text-left hover:bg-slate-100"
      >
        {actualCost != null ? money(Number(actualCost)) : <span className="text-slate-400">Set cost</span>}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <input
        autoFocus
        type="number"
        step="0.01"
        min="0"
        className="w-24 rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') setEditing(false);
        }}
      />
      <Button size="sm" isLoading={mutation.isPending} onClick={save}>
        Save
      </Button>
      <Button size="sm" variant="secondary" type="button" onClick={() => setEditing(false)}>
        Cancel
      </Button>
    </div>
  );
}

// Itemised order lines for one batch, lazily loaded on expand.
function BatchLinesTable({ batchId }: { batchId: string }) {
  const detailQuery = useQuery({
    queryKey: ['procurement-batch', batchId],
    queryFn: () => getProcurementBatch(batchId),
  });

  if (detailQuery.isLoading) {
    return (
      <div className="py-6 text-center">
        <Spinner />
      </div>
    );
  }

  const lines = detailQuery.data?.orderLines ?? [];

  return (
    <Table>
      <Thead>
        <tr>
          <Th>Item</Th>
          <Th>Qty</Th>
          <Th>Charged (per unit)</Th>
          <Th>Oakford cost (per unit)</Th>
        </tr>
      </Thead>
      <tbody>
        {lines.map((line) => (
          <Tr key={line.id}>
            <Td>{line.customerCatalogItem?.name ?? '-'}</Td>
            <Td>{line.quantity}</Td>
            <Td>{money(Number(line.priceSnapshot))}</Td>
            <Td>
              <CostCell orderLineId={line.id} actualCost={line.actualCost} />
            </Td>
          </Tr>
        ))}
        {lines.length === 0 && (
          <Tr>
            <Td colSpan={4} className="text-center text-slate-400">
              No lines on this batch.
            </Td>
          </Tr>
        )}
      </tbody>
    </Table>
  );
}

function BatchRow({ batch, expanded, onToggle }: { batch: ProcurementBatch; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <Tr className="cursor-pointer hover:bg-slate-50" onClick={onToggle}>
        <Td>
          <span className="inline-flex items-center gap-1.5 font-medium text-slate-900">
            {expanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
            {batch.customerOrganisation?.name ?? '-'}
          </span>
        </Td>
        <Td>{batch.site?.name ?? <span className="text-slate-400">Whole org</span>}</Td>
        <Td>{batch.orderLines?.length ?? 0}</Td>
        <Td>{batch.markedOrderedAt ? new Date(batch.markedOrderedAt).toLocaleString() : '-'}</Td>
      </Tr>
      {expanded && (
        <tr>
          <td colSpan={4} className="border-b border-slate-100 bg-slate-50/60 px-4 py-3">
            <BatchLinesTable batchId={batch.id} />
          </td>
        </tr>
      )}
    </>
  );
}

export function ProcurementPage() {
  const [reviewTarget, setReviewTarget] = useState<PendingProcurementGroup | null>(null);
  const [expandedBatchId, setExpandedBatchId] = useState<string | null>(null);

  const pendingQuery = useQuery({ queryKey: ['procurement-pending'], queryFn: listPendingProcurement });
  const batchesQuery = useQuery({ queryKey: ['procurement-batches'], queryFn: listProcurementBatches });

  return (
    <div>
      <PageHeader
        title="Procurement"
        description="Approved order lines ready to be picked up into a batch and marked as ordered with the supplier."
      />

      <Card className="mb-6">
        <CardHeader>
          <span className="text-sm font-medium text-slate-600">Pending, unbatched</span>
        </CardHeader>
        {pendingQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !pendingQuery.data || pendingQuery.data.length === 0 ? (
          <EmptyState icon={PackageSearch} title="Nothing pending" description="Every approved order has been batched." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Customer</Th>
                <Th>Site</Th>
                <Th>Lines</Th>
                <Th>Value</Th>
                <Th />
              </tr>
            </Thead>
            <tbody>
              {pendingQuery.data.map((group) => {
                const key = `${group.customerOrganisationId}:${group.siteId}`;
                return (
                  <Tr key={key}>
                    <Td className="font-medium text-slate-900">{group.customerName}</Td>
                    <Td>{group.siteName}</Td>
                    <Td>{group.lineCount}</Td>
                    <Td>{money(group.totalValue)}</Td>
                    <Td className="text-right">
                      <Button size="sm" onClick={() => setReviewTarget(group)}>
                        Create batch
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <CardHeader>
          <span className="text-sm font-medium text-slate-600">Batch history</span>
        </CardHeader>
        {batchesQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !batchesQuery.data || batchesQuery.data.length === 0 ? (
          <EmptyState icon={ShoppingBag} title="No batches yet" description="Batches you create will show up here." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th>Customer</Th>
                <Th>Site</Th>
                <Th>Lines</Th>
                <Th>Ordered</Th>
              </tr>
            </Thead>
            <tbody>
              {batchesQuery.data.map((batch) => (
                <BatchRow
                  key={batch.id}
                  batch={batch}
                  expanded={expandedBatchId === batch.id}
                  onToggle={() => setExpandedBatchId((current) => (current === batch.id ? null : batch.id))}
                />
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <CreateBatchModal group={reviewTarget} open={!!reviewTarget} onClose={() => setReviewTarget(null)} />
    </div>
  );
}
